import { useState, useRef, useEffect, Suspense, useMemo } from "react";
import { useRecoilState, useRecoilValue } from "recoil";
import { controlGuidanceReportState } from "atoms/atoms-report";
// import { currentControlCatalogState } from "atoms/atoms-global-control";
import { userIdState, projectIdState } from "atoms/atoms-admin";
import PropTypes from "prop-types";
import styled from "styled-components";
// import { DownloadButton } from "components/elements";
import { ExportButton } from "features/brm";
import { ExportTableModal, useExportTable } from "features/exporter";
import { BrmDetailTable } from "brm/tables/BrmTables";
import { /* , dloadJSON, */ handleNoteInTooltip, formattedTimeStamp } from "utils/report-utils";
import { /* REPORT, REPORT_SUFFIX, */ CONTROL_GUIDANCE_DATA } from "constants/brm";
import { LoadingSpinner } from "components/elements";
import useSioService from "hooks/useSioService";
import { useModal } from "hooks";
import { createColumnMappedNoEditRpt, createColumnMappedNoEdit } from "brm/tables/services/column/columnFactory";

// Styles
import * as S from "brm/styles/details-table.styles";

const CntrlGuidanceRptTable = ({ selectedElement, tableTitle }) => {
  // Global state
  const [controlGuidanceReport, setControlGuidanceReport] = useRecoilState(controlGuidanceReportState);
  // const currentControlCatalog = useRecoilValue(currentControlCatalogState);
  const userId = useRecoilValue(userIdState);
  const projectId = useRecoilValue(projectIdState);
  const { disableExport, setExportTableData, handleTableExport } = useExportTable();
  const { isVisible, toggle } = useModal();

  // Local State
  const socket = useRef(null);
  const [sio, isConnected] = useSioService(projectId);
  socket.current = sio;

  // Leave in row selector so user can still highlight rows in the report while viewing
  const [selectedRowIndex, setSelectedRowIndex] = useState("");
  const [inProgress, setInProgress] = useState(true);
  const [generalError, setGeneralError] = useState(false);
  const [errMsg, setErrMsg] = useState(``);
  const [done1, setDone1] = useState(true);

  const analysisResults = useRef([]);
  const progMsg1 = useRef("");

  const columns = useMemo(
    () => [
      createColumnMappedNoEdit("controlId"),
      createColumnMappedNoEdit("familyName"),
      createColumnMappedNoEdit("category"),
      createColumnMappedNoEdit("strength"),
      createColumnMappedNoEditRpt("targetName", handleNoteInTooltip),
      createColumnMappedNoEdit("targetType"),
      createColumnMappedNoEdit("effective"),
      createColumnMappedNoEdit("inBaseline"),
      createColumnMappedNoEditRpt("guidance", handleNoteInTooltip),
    ],
    []
  );

  useEffect(() => {
    if (controlGuidanceReport.summary) {
      const progMsgs = controlGuidanceReport.summary.split(", ");
      [progMsg1.current] = progMsgs;
    }
  }, [controlGuidanceReport.summary]);

  /**
   * Download report
   */
  /* const handleDownloadClick = () => {
    const fileName = selectedElement.name;
    const jsonData = {
      metaData: {
        userId,
        reportType: REPORT.controlGuidance,
        variant: fileName,
        baseline: selectedElement.baseline.name,
        catalog: currentControlCatalog.name,
        summary: controlGuidanceReport.summary,
        reportGenerationDate: controlGuidanceReport.timestamp,
        numOfRows: controlGuidanceReport.count,
      },
      rows: controlGuidanceReport.data,
    };
    dloadJSON(jsonData, `${fileName + REPORT_SUFFIX.controlGuidance}.json`);
  }; */

  /**
   * Analysis Results Handlers
   *
   * Each element type receives its own _prog msg and the _done msg is global and sent only
   * once all of the _progs have been sent.
   */
  useEffect(() => {
    function doWork() {
      // Start the new report
      progMsg1.current = "Getting baseline and allocated controls...";
      setDone1(false);
      socket.current.sendReq(CONTROL_GUIDANCE_DATA, {
        userId,
        projectId,
        variant: selectedElement,
      });
      socket.current.waitProg(CONTROL_GUIDANCE_DATA, (data) => {
        // console.log(`waitProg ${CONTROL_GUIDANCE_DATA} - data: `, data);
        progMsg1.current = data.msg;
      });
      socket.current.waitResp(CONTROL_GUIDANCE_DATA, (data) => {
        // console.log(`waitResp ${CONTROL_GUIDANCE_DATA} - data: `, data);

        // Update the table with the latest results received from backend analysis
        analysisResults.current = data.msg;

        progMsg1.current = "";
        setDone1(true);
        setInProgress(false);

        // Capture the final report
        setControlGuidanceReport({
          data: analysisResults.current,
          summary: "",
          timestamp: formattedTimeStamp(),
          count: analysisResults.current.length.toLocaleString(),
        });
      });
      socket.current.waitErr(CONTROL_GUIDANCE_DATA, (data) => {
        // console.log(`waitErr ${CONTROL_GUIDANCE_DATA} - data.msg: `, data.msg);

        // For now, indicate error message in first item message area
        progMsg1.current = data.msg;
        setGeneralError(true);
      });
    }
    if (isConnected) {
      setGeneralError(false);
      doWork();
    }
  }, [setControlGuidanceReport, setGeneralError, setErrMsg, isConnected, userId, projectId, selectedElement]);

  return (
    <S.DetailsContainer id="CntrlGuidanceRptTable_detailsPanel">
      <S.DetailButtonTopCorner>
        <LeftMsgArea>
          <>
            {inProgress ? (
              <ProgressArea>
                {generalError ? (
                  <Prog>
                    <ProgMsg>{errMsg}</ProgMsg>
                    <DoneMsg />
                  </Prog>
                ) : (
                  <>
                    <Prog>
                      <ProgMsg>{progMsg1.current}</ProgMsg>
                      <DoneMsg>{done1 ? "" : <LoadingSpinner message="" />}</DoneMsg>
                    </Prog>
                  </>
                )}
              </ProgressArea>
            ) : (
              <>
                {generalError && (
                  <Prog>
                    <ProgMsg>{errMsg}</ProgMsg>
                    <DoneMsg />
                  </Prog>
                )}
              </>
            )}
          </>
          <Updated>Last updated: {controlGuidanceReport.timestamp}</Updated>
        </LeftMsgArea>
        {/* TODO: Merge the DownloadButton's functionality (JSON format) into the ExportButton. BRMCD-6385 */}
        {/* <DownloadButton onClick={handleDownloadClick} /> */}
        <ExportButton onClick={() => toggle()} disableExport={disableExport} />
      </S.DetailButtonTopCorner>
      <ExportTableModal onTableExport={handleTableExport} isVisible={isVisible} toggle={toggle} />

      <S.ActionContainer>
        <Suspense fallback="Loading...">
          <S.DetailsTableContainer>
            <BrmDetailTable
              data={controlGuidanceReport.data}
              columns={columns}
              setSelectedRowIndex={setSelectedRowIndex}
              selectedRowIndex={selectedRowIndex}
              showRowSelect={false}
              elementName={selectedElement.name}
              tableTitle={tableTitle}
              setExportTableData={setExportTableData}
            />
          </S.DetailsTableContainer>
        </Suspense>
      </S.ActionContainer>
    </S.DetailsContainer>
  );
};

CntrlGuidanceRptTable.propTypes = {
  selectedElement: PropTypes.object,
  tableTitle: PropTypes.string,
};

const LeftMsgArea = styled.div`
  display: grid;
`;

const ProgressArea = styled.div`
  display: grid;
  grid-template-columns: auto auto auto auto;
  grid-column-gap: 1em;
`;

const Prog = styled.div`
  display: grid;
  grid-template-columns: auto auto;
  grid-column-gap: 0.1em;
`;

const ProgMsg = styled.div`
  justify-self: end;
`;

const DoneMsg = styled.div`
  justify-self: start;
`;

const Updated = styled.div`
  justify-self: end;
`;

export default CntrlGuidanceRptTable;
